import { Form, useFormContext } from "@/components/form";
import { FormFieldWithError } from "@/components/form/form-field-with-error";
import { CollapsibleSection } from "@/components/form/collapsible-section";
import { Dialog, ModalContextExtractor, ModalHeader, useOpenModal } from "@/components/modal";
import { Departamento } from "@/shared/types/departamentos";
import { Ciudad } from "@/shared/types/ciudad";
import DepartamentoManualSearch from "@/features/auxiliar/departamentos/components/departamento-manual-search";
import CiudadManualSearch from "@/features/auxiliar/ciudades/components/ciudad-manual-search";
import { getPais } from "@/features/auxiliar/pais/api/get-pais";
import { getAllPaises } from "@/features/auxiliar/pais/api/get-all-paises";
import SearchIcon from "@mui/icons-material/Search";
import { IconButton, Stack, TextField, Typography } from "@mui/material";
import { useEffect } from "react";
import { useStore } from "@tanstack/react-form";

function LocalUbicacionFields({ prefix, dialogCodeDep, dialogCodeCiu }: {
    prefix: "localSalida" | "localEntrega";
    dialogCodeDep: string;
    dialogCodeCiu: string;
}) {
    const form = useFormContext();
    const openModal = useOpenModal();

    const handleDepartamentoSelect = (departamento: Departamento) => {
        form.setFieldValue(`${prefix}.departamentoId`, departamento.id);
        form.setFieldValue(`${prefix}.departamentoNombre`, departamento.nombre);
        form.setFieldValue(`${prefix}.departamentoCodigo`, departamento.codigo);
        form.setFieldValue(`${prefix}.ciudadId`, "");
        form.setFieldValue(`${prefix}.ciudadNombre`, "");
        form.setFieldValue(`${prefix}.ciudadCodigo`, "");
    };

    const handleCiudadSelect = (ciudad: Ciudad) => {
        form.setFieldValue(`${prefix}.ciudadId`, ciudad.id);
        form.setFieldValue(`${prefix}.ciudadNombre`, ciudad.nombre);
        form.setFieldValue(`${prefix}.ciudadCodigo`, ciudad.codigo);
    };

    return (
        <>
            <Stack direction={{ xs: "column", sm: "row" }} gap={1.5} flexWrap="wrap">
                <Stack flex="2 1 300px" minWidth="240px">
                    <Form.TextField name={`${prefix}.direccion`} label="Dirección *" size="small" fullWidth />
                </Stack>
                <Stack flex="0 1 120px" minWidth="100px">
                    <Form.NumberField name={`${prefix}.numeroCasa`} label="N° Casa *" size="small" decimalScale={0} />
                </Stack>
                <Stack flex="1 1 180px" minWidth="160px" direction="row" alignItems="center" gap={0.5}>
                    <form.Field name={`${prefix}.departamentoNombre`}>
                        {(field) => (
                            <TextField
                                label="Departamento *"
                                size="small"
                                value={field.state.value ?? ""}
                                InputProps={{ readOnly: true }}
                                error={field.state.meta.errors.length > 0 && !field.state.value}
                                helperText={!field.state.value && field.state.meta.errors[0]?.message}
                                fullWidth
                            />
                        )}
                    </form.Field>
                    <IconButton onClick={() => openModal(dialogCodeDep)} color="primary" size="small">
                        <SearchIcon />
                    </IconButton>
                </Stack>
                <Stack flex="1 1 180px" minWidth="160px" direction="row" alignItems="center" gap={0.5}>
                    <form.Field name={`${prefix}.ciudadNombre`}>
                        {(field) => (
                            <TextField
                                label="Ciudad *"
                                size="small"
                                value={field.state.value ?? ""}
                                InputProps={{ readOnly: true }}
                                error={field.state.meta.errors.length > 0 && !field.state.value}
                                helperText={!field.state.value && field.state.meta.errors[0]?.message}
                                fullWidth
                            />
                        )}
                    </form.Field>
                    <form.Field name={`${prefix}.departamentoId`}>
                        {(depField) => (
                            <IconButton
                                onClick={() => openModal(dialogCodeCiu)}
                                color="primary"
                                size="small"
                                disabled={!depField.state.value}
                            >
                                <SearchIcon />
                            </IconButton>
                        )}
                    </form.Field>
                </Stack>
            </Stack>

            <Dialog code={dialogCodeDep} size="md">
                <ModalHeader>Seleccionar Departamento</ModalHeader>
                <ModalContextExtractor>
                    {({ handleClose }) => (
                        <DepartamentoManualSearch
                            padding={3}
                            onDepartamentoSelect={(departamento) => {
                                handleDepartamentoSelect(departamento);
                                handleClose();
                            }}
                        />
                    )}
                </ModalContextExtractor>
            </Dialog>

            <Dialog code={dialogCodeCiu} size="md">
                <ModalHeader>Seleccionar Ciudad</ModalHeader>
                <ModalContextExtractor>
                    {({ handleClose }) => (
                        <form.Subscribe selector={state => (state.values as any)[prefix]?.departamentoId}>
                            {departamentoId => (
                                <CiudadManualSearch
                                    departamentoId={departamentoId}
                                    padding={3}
                                    onCiudadSelect={(ciudad) => {
                                        handleCiudadSelect(ciudad);
                                        handleClose();
                                    }}
                                />
                            )}
                        </form.Subscribe>
                    )}
                </ModalContextExtractor>
            </Dialog>
        </>
    );
}

export default function TransporteFields() {
    const form = useFormContext();

    const paisId = useStore(form.store, state => (state.values as any).transportista?.paisId);

    useEffect(() => {
        if (!paisId) return;
        getPais(paisId).then(pais => {
            if (pais?.codigo) form.setFieldValue("transportista.nacionalidad" as any, pais.codigo);
            if (pais?.nombre) form.setFieldValue("transportista.nacionalidadDescripcion" as any, pais.nombre);
        }).catch(() => { });
    }, [paisId]);

    return (
        <>
            {/* E10 - Datos generales del transporte */}
            <CollapsibleSection title="Datos del Transporte">
                <Stack direction={{ xs: "column", sm: "row" }} gap={1.5} flexWrap="wrap">
                    <Stack flex="1 1 180px" minWidth="160px">
                        <Form.Select name="tipoTransporte" label="Tipo de Transporte *" size="small">
                            <Form.Select.Option value="1">Propio</Form.Select.Option>
                            <Form.Select.Option value="2">Tercero</Form.Select.Option>
                        </Form.Select>
                    </Stack>
                    <Stack flex="1 1 180px" minWidth="160px">
                        <Form.Select
                            name="modalidadTransporte"
                            label="Modalidad *"
                            size="small"
                            onValueChange={(v) => {
                                form.setFieldMeta("nroVuelo" as any, (prev: any) => ({ ...prev, errorMap: { ...prev.errorMap, onSubmit: undefined } }));
                                if (v !== "3") form.setFieldValue("nroVuelo" as any, undefined, { dontUpdateMeta: true });
                            }}
                        >
                            <Form.Select.Option value="1">Terrestre</Form.Select.Option>
                            <Form.Select.Option value="2">Fluvial</Form.Select.Option>
                            <Form.Select.Option value="3">Aéreo</Form.Select.Option>
                            <Form.Select.Option value="4">Multimodal</Form.Select.Option>
                        </Form.Select>
                    </Stack>
                    <Stack flex="1 1 220px" minWidth="180px">
                        <Form.Select name="responsableFlete" label="Responsable del Flete *" size="small" slotProps={{ htmlInput: { sx: { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" } }, popover: { slotProps: { paper: { sx: { minWidth: 300 } } } } }}>
                            <Form.Select.Option value="1">Emisor de la Factura Electrónica</Form.Select.Option>
                            <Form.Select.Option value="2">Receptor de la Factura Electrónica</Form.Select.Option>
                            <Form.Select.Option value="3">Tercero</Form.Select.Option>
                            <Form.Select.Option value="4">Agente intermediario del transporte</Form.Select.Option>
                            <Form.Select.Option value="5">Transporte propio</Form.Select.Option>
                        </Form.Select>
                    </Stack>
                    <Stack flex="1 1 160px" minWidth="140px">
                        <Form.TextField name="fechaInicioTraslado" label="Fecha Inicio Traslado *" size="small" type="date" slotProps={{ inputLabel: { shrink: true } }} />
                    </Stack>
                    <Stack flex="1 1 160px" minWidth="140px">
                        <Form.TextField name="fechaFinTraslado" label="Fecha Fin Traslado *" size="small" type="date" slotProps={{ inputLabel: { shrink: true } }} />
                    </Stack>
                    <form.Field name="modalidadTransporte">
                        {(field) => field.state.value === "3" && (
                            <Stack flex="1 1 140px" minWidth="120px">
                                <Form.TextField name="nroVuelo" label="N° Vuelo *" size="small" slotProps={{ htmlInput: { maxLength: 6 } }} />
                            </Stack>
                        )}
                    </form.Field>
                </Stack>
            </CollapsibleSection>

            {/* E10.1 - Local de salida */}
            <CollapsibleSection title="Local de Salida">
                <LocalUbicacionFields
                    prefix="localSalida"
                    dialogCodeDep="searchDepSalida"
                    dialogCodeCiu="searchCiuSalida"
                />
            </CollapsibleSection>

            {/* E10.2 - Local de entrega */}
            <CollapsibleSection title="Local de Entrega">
                <LocalUbicacionFields
                    prefix="localEntrega"
                    dialogCodeDep="searchDepEntrega"
                    dialogCodeCiu="searchCiuEntrega"
                />
            </CollapsibleSection>

            {/* E10.3 - Vehículo */}
            <CollapsibleSection title="Vehículo de Traslado">
                <Stack direction={{ xs: "column", sm: "row" }} gap={1.5} flexWrap="wrap">
                    <Stack flex="1 1 180px" minWidth="160px">
                        <Form.TextField name="vehiculo.tipoVehiculo" label="Tipo de Vehículo *" size="small" placeholder="Ej: Camión" />
                    </Stack>
                    <Stack flex="1 1 160px" minWidth="140px">
                        <Form.TextField name="vehiculo.marca" label="Marca *" size="small" />
                    </Stack>
                    <Stack flex="1 1 200px" minWidth="180px">
                        <Form.Select name="vehiculo.tipoIdentificacion" label="Tipo de Identificación *" size="small">
                            <Form.Select.Option value="1">Número de Identificación</Form.Select.Option>
                            <Form.Select.Option value="2">Número de Matrícula</Form.Select.Option>
                        </Form.Select>
                    </Stack>
                    <form.Field name="vehiculo.tipoIdentificacion">
                        {(field) => field.state.value === "1" ? (
                            <Stack flex="1 1 180px" minWidth="160px">
                                <Form.TextField name="vehiculo.numeroId" label="N° Identificación *" size="small" />
                            </Stack>
                        ) : (
                            <Stack flex="1 1 180px" minWidth="160px">
                                <Form.TextField name="vehiculo.numeroMatricula" label="N° Matrícula *" size="small" />
                            </Stack>
                        )}
                    </form.Field>
                </Stack>
            </CollapsibleSection>

            {/* E10.4 - Transportista */}
            <CollapsibleSection title="Transportista">
                <Stack direction={{ xs: "column", sm: "row" }} gap={1.5} flexWrap="wrap" alignItems="flex-start">
                    <Stack flex="1 1 180px" minWidth="160px">
                        <Form.Select
                            name="transportista.naturaleza"
                            label="Naturaleza *"
                            size="small"
                            onValueChange={(v) => {
                                if (v === "2") {
                                    form.setFieldMeta("transportista.ruc" as any, (prev: any) => ({ ...prev, errorMap: { ...prev.errorMap, onSubmit: undefined } }));
                                    form.setFieldMeta("transportista.dv" as any, (prev: any) => ({ ...prev, errorMap: { ...prev.errorMap, onSubmit: undefined } }));
                                } else {
                                    form.setFieldMeta("transportista.tipoDocumento" as any, (prev: any) => ({ ...prev, errorMap: { ...prev.errorMap, onSubmit: undefined } }));
                                    form.setFieldMeta("transportista.numeroDocumento" as any, (prev: any) => ({ ...prev, errorMap: { ...prev.errorMap, onSubmit: undefined } }));
                                }
                            }}
                        >
                            <Form.Select.Option value="1">Contribuyente</Form.Select.Option>
                            <Form.Select.Option value="2">No Contribuyente</Form.Select.Option>
                        </Form.Select>
                    </Stack>
                    <Stack flex="2 1 250px" minWidth="200px">
                        <Form.TextField name="transportista.nombre" label="Nombre / Razón Social *" size="small" fullWidth />
                    </Stack>

                    <form.Field name="transportista.naturaleza">
                        {(field) => field.state.value === "1" ? (
                            <>
                                <FormFieldWithError
                                    name="transportista.ruc"
                                    label="RUC *"
                                    size="small"
                                    width={{ xs: "100%", sm: "160px" }}
                                />
                                <FormFieldWithError
                                    name="transportista.dv"
                                    label="DV *"
                                    size="small"
                                    width={{ xs: "100%", sm: "90px" }}
                                    fieldKind="number"
                                />
                            </>
                        ) : (
                            <>
                                <Stack flex="1 1 200px" minWidth="180px">
                                    <Form.Select name="transportista.tipoDocumento" label="Tipo Documento *" size="small">
                                        <Form.Select.Option value="1">Cédula paraguaya</Form.Select.Option>
                                        <Form.Select.Option value="2">Pasaporte</Form.Select.Option>
                                        <Form.Select.Option value="3">Cédula extranjera</Form.Select.Option>
                                        <Form.Select.Option value="4">Carnet de residencia</Form.Select.Option>
                                    </Form.Select>
                                </Stack>
                                <Stack flex="1 1 180px" minWidth="160px">
                                    <Form.TextField name="transportista.numeroDocumento" label="N° Documento *" size="small" />
                                </Stack>
                            </>
                        )}
                    </form.Field>
                </Stack>

                <Stack direction={{ xs: "column", sm: "row" }} gap={1.5} flexWrap="wrap" mt={2}>
                    <Stack flex="2 1 220px" minWidth="180px">
                        <Form.ModelComboBoxAsync
                            name={"transportista.paisId" as any}
                            label="Nacionalidad (País)"
                            get={getPais}
                            getAll={getAllPaises}
                            size="small"
                        />
                    </Stack>
                    <Stack flex="2 1 240px" minWidth="200px">
                        <Form.TextField name="transportista.domicilioFiscal" label="Domicilio Fiscal" size="small" fullWidth />
                    </Stack>
                </Stack>

                <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>Datos del Chofer</Typography>
                <Stack direction={{ xs: "column", sm: "row" }} gap={1.5} flexWrap="wrap" mt={0.5}>
                    <Stack flex="1 1 180px" minWidth="160px">
                        <Form.TextField name="transportista.numeroDocChofer" label="N° Doc. Chofer *" size="small" />
                    </Stack>
                    <Stack flex="2 1 250px" minWidth="200px">
                        <Form.TextField name="transportista.nombreChofer" label="Nombre y Apellido Chofer *" size="small" fullWidth />
                    </Stack>
                    <Stack flex="2 1 250px" minWidth="200px">
                        <Form.TextField name="transportista.direccionChofer" label="Dirección del Chofer *" size="small" fullWidth />
                    </Stack>
                </Stack>
            </CollapsibleSection>
        </>
    );
}
